<script setup lang="ts">
  import { computed, onMounted, ref, watch, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { useRoute } from 'nuxt/app'

  import { ChevronLeftIcon, SearchLgIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { MaterialVideoLibraryIcon } from '@gohighlevel/ghl-icons/24/material/rounded'

  import NotificationWeb from '@common/notification-layer/components/NotificationWeb.vue'

  import CPAvatar from '@ui/components/CPAvatar.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPInput from '@ui/components/CPInput.vue'

  import CPSkeleton from '@ui/components/CPSkeleton.vue'

  import TopBarChatButton from './TopBarChatButton.vue'

  import ProfileCard from './ProfileCard.vue'

  import { useLessonNavigatorDrawer } from '../composables/useLessonNavigatorDrawer'

  import { useAuth } from '../../auth-layer/composables/useAuth'

  import { AuthStatus } from '../../auth-layer/store/auth.store'

  import { GOKOLLAB_ASSETS } from '../../core-layer'

  import { createElementId } from '../../core-layer/utils/core.utils'

  import { useCurrentUser } from '../../current-user-layer/composables/useCurrentUser'

  import { useCPNavigation } from '../../navigation-layer/composables/useNavigationContext'

  const props = defineProps({
    showBottomBorder: {
      type: Boolean,
      default: false,
    },
    showLogo: {
      type: Boolean,
      default: true,
    },
    showRightContent: {
      type: Boolean,
      default: true,
    },
    showAuthSection: {
      type: Boolean,
      default: true,
    },
    showNotifications: {
      type: Boolean,
      default: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    showChatButton: {
      type: Boolean,
      default: false,
    },
    chatUnreadCount: {
      type: Number,
      default: 0,
    },
    /** When opening search, pre-fill with this (e.g. route.query.q when already on search page) */
    initialSearchQuery: {
      type: String,
      default: '',
    },
    /** When true, show a button that opens the lesson navigator drawer (course lesson page on mobile) */
    showLessonNavigatorButton: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, do not auto-open the generic inline search input on routes whose
     * path contains `/search` (e.g. discovery search results). The host shell
     * provides its own search bar in the `#center` slot instead.
     */
    disableInlineSearchMode: {
      type: Boolean,
      default: false,
    },
    /**
     * Discovery search results: render only `#center` (full-width search bar), hiding
     * logo, menu, notifications, and auth — matches production GoKollab search header.
     */
    searchBarOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Brand logo `src` for the topbar avatar. Defaults to the standard
     * GoKollab K. Callers in branded contexts (e.g. GoKollab Pro shell) can
     * pass a context-aware logo so this shared component stays free of
     * domain-specific branding lookups.
     */
    logoSrc: {
      type: String,
      default: GOKOLLAB_ASSETS.LOGO.K_WITHOUT_BORDER,
    },
    /** Portal / brand logo shape — use `square` for location portal icons (matches CPPortalSidebar). */
    logoShape: {
      type: String as PropType<'circle' | 'square'>,
      default: 'circle',
    },
    /** GoKollab user-location provisioned — gates manage payments in profile menu. */
    hasUserLocation: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    (e: 'loginClick'): void
    (e: 'signupClick'): void
    (e: 'logout'): void
    (e: 'searchSubmit', query: string): void
    (e: 'searchInputChange', value: string): void
    (e: 'searchOpen'): void
    (e: 'searchClose'): void
    (e: 'chatClick'): void
  }>()

  const route = useRoute()

  const { store: currentUserStore } = useCurrentUser()

  const { store: authStore } = useAuth()

  const lessonNavigatorDrawer = useLessonNavigatorDrawer()

  const { t } = useI18n()

  const { CPNavigateTo } = useCPNavigation()

  // Auth and user state
  const userProfile = computed(() => {
    return currentUserStore?.profile
  })

  const isAuthenticated = computed(() => {
    return authStore?.isAuthenticated || false
  })

  const isAuthLoading = computed(() => {
    if (!authStore) return true
    return authStore.status === AuthStatus.LOADING
  })

  const notificationListIsOpen = ref(false)

  const appLauncherIsOpen = ref(false)

  const profileCardIsOpen = ref(false)

  const isSearchMode = ref(false)

  const searchQuery = ref('')

  /** True while user is actively typing; prevents URL-derived prop from overwriting local input */
  let isUserTyping = false

  /** True if user submitted a search in this search session (since last openSearch) */
  const searchSubmittedThisSession = ref(false)

  const openSearch = () => {
    if (props.disableInlineSearchMode) {
      emit('searchOpen')
      return
    }
    isSearchMode.value = true
    searchQuery.value = props.initialSearchQuery || ''
    searchSubmittedThisSession.value = false
    emit('searchOpen')
  }

  /** Close search UI only (no emit). Use when route changed and we're just syncing UI. */
  const closeSearchUI = () => {
    isSearchMode.value = false
    searchQuery.value = ''
    searchSubmittedThisSession.value = false
  }

  const closeSearch = () => {
    const hadQuery = searchQuery.value.trim().length > 0
    isSearchMode.value = false
    searchQuery.value = ''
    if (searchSubmittedThisSession.value || isInlineMobileSearchRoute(route.path) || hadQuery) {
      emit('searchClose')
    }
    searchSubmittedThisSession.value = false
  }

  const onSearchInputUpdate = (value: string) => {
    searchQuery.value = value
    isUserTyping = true
    emit('searchInputChange', value)
  }

  const handleSearchSubmit = () => {
    const query = searchQuery.value.trim()
    if (query) {
      searchSubmittedThisSession.value = true
      emit('searchSubmit', query)
    }
    // Do not close search so it stays open after navigating to search page (e.g. courses-search)
  }

  const toggleNotificationList = () => {
    profileCardIsOpen.value = false
    appLauncherIsOpen.value = false
    notificationListIsOpen.value = !notificationListIsOpen.value
  }

  const handleLogoClick = async () => {
    await CPNavigateTo({
      name: 'index',
    })
  }

  /** `/search-input` contains the substring `/search` but uses its own page-level search bar. */
  const isDedicatedMobileSearchInputRoute = (path: string) => path === '/search-input'

  const isInlineMobileSearchRoute = (path: string) => path.includes('/search') && !isDedicatedMobileSearchInputRoute(path)

  onMounted(() => {
    if (props.disableInlineSearchMode) {
      return
    }
    if (isInlineMobileSearchRoute(route.path) || props.initialSearchQuery.trim().length > 0) {
      isSearchMode.value = true
      searchQuery.value = props.initialSearchQuery || ''
    }
  })

  watch(
    () => route.path,
    () => {
      if (props.disableInlineSearchMode) {
        if (isSearchMode.value) {
          closeSearchUI()
        }
        return
      }
      if (isInlineMobileSearchRoute(route.path) && !isSearchMode.value) {
        isSearchMode.value = true
        searchQuery.value = props.initialSearchQuery || ''
      } else if (!isInlineMobileSearchRoute(route.path) && isSearchMode.value) {
        closeSearchUI()
      }
    }
  )

  watch(
    () => props.initialSearchQuery,
    value => {
      if (!isSearchMode.value && value?.trim().length) {
        isSearchMode.value = true
        searchQuery.value = value
        return
      }

      if (isSearchMode.value) {
        if (isUserTyping) {
          isUserTyping = false
          return
        }
        searchQuery.value = value || ''
      }
    }
  )
</script>

<template>
  <header
    class="bg-background sticky top-0 z-40"
    :class="[showBottomBorder ? 'border border-solid border-[var(--cp-brand-border)] border-b-1 border-t-0 border-l-0 border-r-0' : '']"
  >
    <div class="flex h-13 items-center gap-4 px-4">
      <!-- Discovery search results: search bar only (production mobile search header) -->
      <template v-if="searchBarOnly">
        <div v-if="$slots.center" class="flex min-w-0 flex-1 w-full items-center">
          <slot name="center" />
        </div>
      </template>

      <!-- Search mode: back + input + clear (X) when has text -->
      <template v-else-if="isSearchMode">
        <CPInput
          id="topbar-mobile-search"
          :model-value="searchQuery"
          :placeholder="t('common.ui.searchPlaceholder')"
          size="sm"
          class="flex-1 min-w-0"
          @update:model-value="onSearchInputUpdate"
          @keydown.enter.prevent="handleSearchSubmit"
        >
          <template #prefix>
            <span :id="createElementId('topbar', 'mobile', 'search-back')" class="flex items-center" @click="closeSearch">
              <ChevronLeftIcon class="size-6" />
            </span>
          </template>
        </CPInput>
      </template>

      <!-- Normal mode: logo, slots, notifications, auth -->
      <template v-else>
        <!-- Left Section: Logo + Header + Left Slots -->
        <div class="flex items-center gap-2 flex-1 min-w-0">
          <!-- Default logo avatar, or #logo slot override (e.g. gradient wordmark on GoKollab mobile) -->
          <div
            v-if="showLogo && !$slots.logo"
            class="flex-shrink-0 cursor-pointer"
            role="button"
            tabindex="0"
            :aria-label="t('common.ui.goToHome')"
            @click="handleLogoClick"
            @keydown.enter="handleLogoClick"
          >
            <CPAvatar
              :src="logoSrc"
              :shape="logoShape"
              :alt="t('common.ui.gokollabLogo')"
              name="GoKollab"
              class="object-contain !bg-transparent"
              size="sm"
            />
          </div>

          <div v-if="showLogo && $slots.logo" class="flex-shrink-0">
            <slot name="logo" />
          </div>

          <!-- Left Slots -->
          <div v-if="$slots.left" class="flex items-center gap-2 flex-shrink-0">
            <slot name="left" />
          </div>

          <!-- Header/Title Slot - Next to logo -->
          <div v-if="$slots.center" class="flex items-center min-w-0">
            <slot name="center">
              <slot name="center-content" />
            </slot>
          </div>
        </div>

        <!-- Right Section: Right Slots + Fixed Elements - Only show if showRightContent is true -->
        <div v-if="showRightContent" class="flex items-center justify-end gap-4 flex-shrink-0">
          <!-- Right Slots -->
          <div v-if="$slots.right" class="flex items-center gap-2">
            <slot name="right" />
          </div>

          <!-- Fixed Action Buttons -->
          <div class="flex items-center gap-2">
            <slot name="right-filter" />
            <!-- Lesson navigator drawer trigger (course lesson page) -->
            <CPButton
              v-if="showLessonNavigatorButton"
              :id="createElementId('topbar', 'mobile', 'lesson-navigator')"
              type="ghost"
              size="sm"
              class="flex-shrink-0 p-1 rounded-md"
              color="transparent"
              :aria-label="t('courses.selectLesson')"
              @click="lessonNavigatorDrawer.open()"
            >
              <MaterialVideoLibraryIcon class="size-5" />
            </CPButton>
            <TopBarChatButton
              v-if="props.showChatButton && isAuthenticated && userProfile?.fullName"
              button-id="topbar-mobile-chat-button"
              badge-id="topbar-mobile-chat-unread-badge"
              :unread-count="props.chatUnreadCount"
              button-class="chat-icon-button !h-10 !w-10 !min-w-0 !rounded-full !p-0 transition-colors duration-200 hover:!bg-[var(--cp-background-100)]"
              @click="emit('chatClick')"
            />
            <ClientOnly v-if="isAuthenticated && userProfile?.fullName">
              <slot name="announcements" />
            </ClientOnly>
            <!-- Search - Only show if showSearch is true -->
            <CPButton
              v-if="showSearch"
              :id="createElementId('topbar', 'mobile', 'search')"
              type="ghost"
              size="sm"
              class="flex-shrink-0 p-1 rounded-md"
              color="transparent"
              :aria-label="t('common.ui.searchPlaceholder')"
              @click="openSearch"
            >
              <SearchLgIcon class="size-5" />
            </CPButton>
            <!-- Notifications - Only show if showNotifications is true -->
            <ClientOnly v-if="showNotifications">
              <NotificationWeb
                v-if="isAuthenticated && userProfile?.fullName"
                :profile-card-is-open="profileCardIsOpen"
                :app-launcher-is-open="appLauncherIsOpen"
                :notification-list-is-open="notificationListIsOpen"
                :position="'end'"
                @update:notificationListIsOpen="toggleNotificationList"
              />
            </ClientOnly>
            <!-- Auth Section - Only show if showAuthSection is true -->
            <template v-if="showAuthSection">
              <!-- Auth Loading State -->
              <template v-if="isAuthLoading">
                <div class="flex items-center gap-2">
                  <CPSkeleton type="button" size="sm" width="64" height="32" />
                  <CPSkeleton type="button" size="sm" width="64" height="32" />
                </div>
              </template>

              <!-- Not Authenticated State -->
              <template v-else-if="!isAuthenticated">
                <slot name="action-buttons">
                  <CPButton id="login" size="xs" type="secondary" @click="emit('loginClick')">
                    {{ t('common.buttons.login') }}
                  </CPButton>
                  <CPButton id="signup" size="xs" type="primary" @click="emit('signupClick')">
                    {{ t('common.buttons.signUp') }}
                  </CPButton>
                </slot>
              </template>
              <!-- Authenticated State -->
              <template v-else>
                <ProfileCard
                  :profile-card-is-open="profileCardIsOpen"
                  :has-user-location="hasUserLocation"
                  @update:profileCardIsOpen="profileCardIsOpen = $event"
                  @logout="emit('logout')"
                >
                  <template #profile-menu-extras-banner="{ close }">
                    <slot name="profile-menu-extras-banner" :close="close" />
                  </template>
                  <template #profile-menu-extras-items="{ close }">
                    <slot name="profile-menu-extras-items" :close="close" />
                  </template>
                </ProfileCard>
              </template>
            </template>
          </div>
        </div>
      </template>
    </div>
  </header>
</template>

<style scoped>
  /* Prevent iOS Safari input-zoom when focusing the mobile search input. */
  :deep(#topbar-mobile-search input) {
    font-size: 16px !important;
  }
</style>
